'use client';

import { SESSION_STATUS } from '@/constants/auth';
import { PropsWithChildren, useEffect, useState, useTransition } from 'react';
import { useSession } from 'next-auth/react';
import { useSWRConfig } from 'swr';
import { type UserProfileDto } from '@/authApiClient';
import { PostLoginFlow } from './PostLoginFlow';
import { PostSignUpFlow } from './PostSignUpFlow';
import { usePrivate } from './PrivateContext';
import {
  useDatadogCorrelation,
  useInitializeContext,
  useRoleRedirect,
  useDatadogUser,
} from '@/hooks';
import { useLaunchDarklyUser } from '@/hooks/useLaunchDarklyUser';
import { PublicSearchSyncNotificationsProvider } from '@/providers/PublicSearchSyncNotificationsProvider';

interface PrivateProps extends PropsWithChildren {
  onRenderPublicSearchStateChange?: (shouldRenderPublicSearch: boolean) => void;
}

// Private mounts unconditionally from `PrivateGate` and always returns
// `children` — its tree position does not change when auth resolves, so the
// route subtree (and any state it holds) survives the LOADING →
// AUTHENTICATED transition. The auth-dependent side effects (PostLoginFlow,
// PostSignUpFlow, the context bootstrap) self-gate on `useSession()` and
// no-op while the session is loading or unauthenticated.

export function Private({
  children,
  onRenderPublicSearchStateChange = () => {},
}: PrivateProps) {
  const { data: session, status } = useSession();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { mutate } = useSWRConfig();
  const [loadedUserProfile, setLoadedUserProfile] = useState<UserProfileDto>();

  const contextData = usePrivate();
  const {
    userOrganisations,
    setUserOrganisations,
    selectedOrganisationId,
    setSelectedOrganisationId,
    setSelectedOrganisation,
    setShowSplashScreen,
    setContextData,
  } = contextData;

  const [isPending] = useTransition();

  useEffect(() => {
    if (isAuthenticated && session?.user && setContextData) {
      setContextData((prevData) => ({
        ...prevData,
        userId: session.user.internalUserId,
        userGuid: session.user.userGuid,
        userProfileGuid: session.user.userProfileGuid,
        userEmail: session.user.email ?? undefined,
        isABRIStaff: session.user.isABRIStaff,
        entitlements: session.user.entitlements,
      }));
    }
    // NB: `userProfileError` is deliberately NOT reset here. This effect
    // re-runs on every `session` ref change (including benign NextAuth
    // refetches), so clearing the flag here would flip a still-failing profile
    // back to "loading" and flicker the header spinner. `PostLoginFlow` owns
    // the flag (it re-asserts it every render from the fetch/session state),
    // and the unauthenticated cleanup below resets it on logout.
  }, [isAuthenticated, session, setContextData]);

  // Clear the lifted user state when the session ends. The provider that owns
  // this state lives above <Shell> and survives logout navigation, so without
  // an explicit reset a previous user's data would leak into the next sign-in.
  useEffect(() => {
    if (status !== SESSION_STATUS.UNAUTHENTICATED) return;

    if (setContextData) {
      setContextData((prevData) => ({
        ...prevData,
        userId: undefined,
        userGuid: undefined,
        userProfileGuid: undefined,
        userEmail: undefined,
        isABRIStaff: undefined,
        entitlements: undefined,
        userProfile: undefined,
        userProfileError: false,
      }));
    }
    // `userOrganisations`, the selected-org state, and `loadedUserProfile` live
    // outside `contextData` (in the provider state and this component's local
    // state respectively), so reset them explicitly — otherwise the next
    // sign-in briefly shows the previous user's organisation list / selection,
    // and `useDatadogUser` keeps reporting the previous user's profile to RUM.
    setUserOrganisations?.(undefined);
    setSelectedOrganisationId?.(undefined);
    setSelectedOrganisation?.(undefined);
    // Reset on an auth transition (sign-out) — a legitimate state
    // synchronisation, not a render-derived value.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadedUserProfile(undefined);
  }, [
    status,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
    setSelectedOrganisation,
  ]);

  useDatadogCorrelation();
  useInitializeContext(
    selectedOrganisationId,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  );
  useDatadogUser(loadedUserProfile, contextData, selectedOrganisationId);
  useLaunchDarklyUser();

  const handleUserProfileLoaded = (userProfile: UserProfileDto) => {
    setLoadedUserProfile(userProfile);
    if (setContextData) {
      setContextData((prevContextData) => ({
        ...prevContextData,
        userProfile,
        userProfileError: false,
        reloadUserProfile: () => {
          mutate(`/api/UserProfile/${prevContextData?.userId}`);
        },
      }));
    }
  };

  const handleUserProfileError = (hasError: boolean) => {
    if (!setContextData) return;
    setContextData((prevContextData) => {
      // When erroring, drop any stale profile so the header shows its degraded
      // fallback (email) rather than outdated data. On recovery `PostLoginFlow`
      // re-applies the freshly fetched profile.
      const nextUserProfile = hasError
        ? undefined
        : prevContextData.userProfile;
      if (
        prevContextData.userProfileError === hasError &&
        prevContextData.userProfile === nextUserProfile
      ) {
        return prevContextData;
      }
      return {
        ...prevContextData,
        userProfileError: hasError,
        userProfile: nextUserProfile,
      };
    });
  };

  const redirectByRole = useRoleRedirect(
    selectedOrganisationId,
    onRenderPublicSearchStateChange,
  );

  useEffect(() => {
    if (isAuthenticated && !isPending && setShowSplashScreen) {
      setShowSplashScreen(false);
    }
  }, [isAuthenticated, isPending, setShowSplashScreen]);

  // children come first in the fragment so their reconciliation index stays
  // at 0 regardless of whether the auth-gated siblings are mounted. Adding
  // siblings before children would shift every subsequent index by one and
  // force React to remount the route subtree when auth resolves.
  return (
    <>
      <PublicSearchSyncNotificationsProvider>
        {children}
      </PublicSearchSyncNotificationsProvider>
      {isAuthenticated && (
        <PostLoginFlow
          onUserProfileLoaded={handleUserProfileLoaded}
          onUserProfileError={handleUserProfileError}
          onUserOrgAssociationsRetrieved={redirectByRole}
        />
      )}
      {isAuthenticated && <PostSignUpFlow onFinishFlow={redirectByRole} />}
    </>
  );
}
